import { track } from '@eventbrite/datalayer-library';
import { STYLE_APPLE_PAY, STYLE_FILL } from '@eventbrite/eds-button';
import { GenericLazyString } from '@eventbrite/i18n';
import {
    ButtonColorOption,
    ButtonShapeOption,
} from '@eventbrite/paypal-checkout-types';
import get from 'lodash/get';
import memoize from 'lodash/memoize';
import React from 'react';
import { GooglePayButton } from '../../components/paymentMethodForms/GooglePayButton';
import { ConnectedPaypalButton } from '../../components/paymentMethodForms/PaypalPaymentFields/ConnectedPaypalButton';
import { ConnectedStripeButton } from '../../components/paymentMethodForms/StripeButton';
import {
    BUY_ON_MAP_FORM_NAME,
    CHECKOUT_FORM_NAME,
    CTA_BUY_APPLE_PAY,
    CTA_CHECKOUT_TEXT,
    CTA_COMPLETE_FREE_TEXT,
    CTA_COMPLETE_PAID_TEXT,
    CTA_JOIN_WAITLIST,
    CTA_SETUP_APPLE_PAY,
    TICKETS_SELECTION_PAGE_FORM_NAME,
    TICKET_CLASS_CATEGORIES_BY_ORDER,
} from '../../constants';
import { HeapEventName } from '../../constants/heap';
import {
    AVAILABLE_TICKET_STATUS,
    SOLD_OUT,
    TICKET_NOT_YET_ON_SALE,
} from '../../containers/ticketSelection/constants';
import {
    allTicketsFree,
    hasEnteredDonationAmount,
    ticketClassesByCategory,
} from '../../containers/ticketSelection/utils/ticketUtils';
import { PaymentMethodType } from '../../models/paymentMethods/enums';
import { Ticket, TicketId, TicketVariant } from '../../models/tickets';
import {
    getBuyerEmailFromSurveyData,
    getBuyerFirstNameFromSurveyData,
    getBuyerLastNameFromSurveyData,
    hasFormServerError,
} from '../../selectors/forms';
import { CheckoutState } from '../../state';
import getTerminology from '../../utils/terminology';
import {
    getIsAppRebrandActive,
    getPaymentsLaunchStripeElementsFF,
} from '../app';
import { getCheckoutGroups } from '../event';
import { isFormSubmitting, isSubmitDisabled } from '../forms';
import { getHasRetryPaymentError } from '../order/typedOrderSelectors';
import { getSelectedPaymentMethod } from '../payment';
import { getRoutePathname } from '../routing';
import { createSelector } from '../utils/selector';
import { getGroupEligibleTypeTickets } from './getGroupEligibleTypeTickets';
import { getIndividualEligibleTypeTickets } from './getIndividualEligibleTypeTickets';
import { getSelectedTickets } from './getSelectedTickets';
import { hasSelectedTickets } from './hasSelectedTickets';
import { getTicketIds, getTicketsById } from './pureSelectors';

export { getHasAddonsTicketsAvailable } from './getHasAddonsTicketsAvailable';
export { getHasAddonsTicketsWithMoreThanOneVariant } from './getHasAddonsTicketsWithMoreThanOneVariant';
export { getTicketsById } from './pureSelectors';

export const getIsMultiTicketEvent = (state: CheckoutState) => {
    const ticketCount = getTicketIds(state).length;
    return ticketCount > 1;
};

/**
 * Composable function who's inner returned function with specific formatting logic for ticket class
 * category
 *
 * @param key Ticket class category, one of "admission", "add_on", or "donation"
 */
export const getTicketClassFormatter = (key: string) =>
    memoize((state) => ticketClassesByCategory(key)(state));

export const getTicketClassCheckoutGroupId = (ticketClasses: Array<Ticket>) =>
    ticketClasses[0].checkoutGroupId;

/**
 * Compare function that sorts the available tickets/variants before the unavailable ones.
 *
 * @returns -1 if `lhs` should appear before `rhs`, 1 if `rhs` should appear before `lhs`, 0 otherwise.
 */
const compareTicketsOrVariants = (
    lhs: Ticket | TicketVariant,
    rhs: Ticket | TicketVariant,
) => {
    // onSaleStatus will be `null` for ticket sections. In that case we will check the first ticket (variant) in that section.
    // Note that the variants are already sorted according to this "available first" criteria so we only need to focus
    // on the first variant.
    const lhsOnSaleStatus =
        lhs.onSaleStatus ?? (lhs as Ticket).variants?.[0]?.onSaleStatus;
    const rhsOnSaleStatus =
        rhs.onSaleStatus ?? (rhs as Ticket).variants?.[0]?.onSaleStatus;

    // Higher weight means the element should appear sooner.
    const weightForStatus: Record<string, number> = {
        [AVAILABLE_TICKET_STATUS]: 75,
        [TICKET_NOT_YET_ON_SALE]: 50,
        [SOLD_OUT]: -1,
    };
    const lhsWeight = weightForStatus[lhsOnSaleStatus] ?? 0;
    const rhsWeight = weightForStatus[rhsOnSaleStatus] ?? 0;

    // Comparator functions return -1 when `lhs` should be _before_ `rhs`.
    // Comparator functions return +1 when `lhs` should be _after_ `rhs`.
    // Comparator functions return 0 when `lhs` and `rhs` are already sorted.
    return rhsWeight - lhsWeight;
};

export const partitionTicketClassesByDiscountId = (
    ticketClasses: Array<Ticket>,
) => {
    const result: {
        tickets: Array<Ticket>;
        ticketsWithAppliedCode: Array<Ticket>;
    } = {
        tickets: [],
        ticketsWithAppliedCode: [],
    };

    for (const ticketClass of ticketClasses) {
        const variants = [...(ticketClass.variants ?? [])].sort(
            compareTicketsOrVariants,
        );

        const ticketToAdd: Ticket = {
            ...ticketClass,
            variants,
        };

        if (
            ticketClass.discountId ||
            ticketClass.variants?.some(
                ({ discountType }) => discountType === 'coded',
            )
        ) {
            result.ticketsWithAppliedCode.push(ticketToAdd);
        } else {
            result.tickets.push(ticketToAdd);
        }
    }

    result.ticketsWithAppliedCode.sort(compareTicketsOrVariants);
    result.tickets.sort(compareTicketsOrVariants);

    return result;
};

export const getGroupRegistrationTickets = (state: CheckoutState) => {
    const isIndividualSelected = get(
        state,
        'groupRegistration.isIndividualSelected',
    );

    return isIndividualSelected
        ? getIndividualEligibleTypeTickets(state)
        : getGroupEligibleTypeTickets(state);
};

/**
 * Returns an array of tickets available based on the users registration type
 * This method takes in a partial group of tickets which are prefiltered by getTicketClassFormatter
 */
export const getEligibleAdvancedTeamTickets = (
    ticketClasses: Record<TicketId, Ticket>,
    isIndividualSelected: boolean,
) => {
    if (isIndividualSelected) {
        return getIndividualEligibleTypeTickets({
            tickets: { ticketsById: ticketClasses },
        } as unknown as CheckoutState);
    }

    return getGroupEligibleTypeTickets({
        tickets: { ticketsById: ticketClasses },
    } as unknown as CheckoutState);
};

/**
 * Formats ticket class data for display in ticket selection page components
 *
 * @param state
 * @returns Object TicketClassesFormattedByCategoryForDisplay
 */
export const getFormattedTicketClassesByCategory = memoize(
    (state: CheckoutState) => {
        const result: Record<
            string,
            {
                tickets: Array<Ticket>;
                ticketsWithAppliedCode: Array<Ticket>;
                title: GenericLazyString | string;
            }
        > = {};

        for (const category of TICKET_CLASS_CATEGORIES_BY_ORDER) {
            let ticketClasses = getTicketClassFormatter(category)(state);

            if (get(state, 'event.hasAdvancedTeamsEnabled')) {
                const isIndividualSelected = get(
                    state,
                    'groupRegistration.isIndividualSelected',
                );

                ticketClasses = getEligibleAdvancedTeamTickets(
                    ticketClasses as unknown as Record<TicketId, Ticket>,
                    isIndividualSelected,
                );
            }

            if (!ticketClasses.length) {
                continue;
            }

            // Note: Default titles are supported by our v3 API and could be retrieved that way.
            // For now I've hard-coded these as constants since they are very unlikely to change and it
            // saves an extra remote trip. See:
            // https://eventbriteapiv3.docs.apiary.io/#reference/event-texts/retrieve/retrieve-event-texts

            const defaultTitle = getTerminology(!!state.app.isRegEvent)
                .ticketCategoryTitle[category];
            const checkoutGroupId =
                getTicketClassCheckoutGroupId(ticketClasses);
            const group = getCheckoutGroups(state).find(
                (currentGroup) => currentGroup.id === checkoutGroupId,
            );

            result[category] = {
                ...partitionTicketClassesByDiscountId(ticketClasses),
                title: group ? group.title : defaultTitle,
            };
        }

        return result;
    },
    ({ _form, ...state }: CheckoutState & { _form?: unknown }) =>
        JSON.stringify(state),
);

// TODO: Continue grouping / alpha sorting.
// Everything below this line is sorted alphabetical ASC

export const getAllVariants = createSelector(getTicketsById, ((
    ticketsById: Record<TicketId, Ticket>,
) =>
    Object.keys(ticketsById).reduce((acc, ticketId) => {
        ticketsById[ticketId].variants.forEach((variant) => {
            // eslint-disable-next-line no-param-reassign
            acc[variant.id] = variant;
        });

        return acc;
    }, {} as Record<string, TicketVariant>)) as unknown as any);

export const getReservedTicketSelectionSubmitErrors = createSelector(
    (state: CheckoutState) =>
        get(state, `form[${TICKETS_SELECTION_PAGE_FORM_NAME}].error`),
    (state: CheckoutState) => get(state, `form[${BUY_ON_MAP_FORM_NAME}].error`),

    ((
        ticketSelectionFormSubmitErrors: string,
        buyOnMapSubmitErrors: string,
    ) => {
        const errors = [];

        if (ticketSelectionFormSubmitErrors) {
            errors.push(ticketSelectionFormSubmitErrors);
        }

        if (buyOnMapSubmitErrors) {
            errors.push(buyOnMapSubmitErrors);
        }

        return errors;
    }) as unknown as any,
);

export const getShouldShowTicketCategoryGroupHeaders = (state: CheckoutState) =>
    Object.keys(getFormattedTicketClassesByCategory(state)).length >= 2;

export const getEventCapacityWaitlistTicketsPageCtaText = (
    state: CheckoutState,
) => {
    const {
        tickets: { ticketsById },
    } = state;

    if (hasEnteredDonationAmount(ticketsById)) {
        return CTA_CHECKOUT_TEXT;
    }

    return CTA_JOIN_WAITLIST;
};

const trackOnce = memoize(track, (...args) => JSON.stringify(args));

type CTAButtonProps =
    | {
          type: any;
          text: GenericLazyString;
      }
    | {
          node: JSX.Element;
          text?: GenericLazyString;
      };

export const getCheckoutDefaultCtaButtonProps = (
    state: CheckoutState,
): CTAButtonProps => {
    const { applepay } = state;
    const selectedTickets = getSelectedTickets(state);
    const showFreeCta = allTicketsFree(selectedTickets);
    const selectedPaymentMethod = getSelectedPaymentMethod(state);
    const isAppRebrandActive = getIsAppRebrandActive(state);
    const isCheckoutFormEnabled = !isSubmitDisabled(state, CHECKOUT_FORM_NAME);
    const hasCheckoutFormServerError = hasFormServerError(
        state,
        CHECKOUT_FORM_NAME,
    );
    const isCheckoutFormSubmitting = isFormSubmitting(
        state,
        CHECKOUT_FORM_NAME,
    );
    const isPayPalSelected =
        selectedPaymentMethod === PaymentMethodType.NONCE_BRAINTREE;

    const isPayPalButtonDisabled = !hasSelectedTickets(state);

    let ctaButtonProps: CTAButtonProps = {
        type: STYLE_FILL,
        text: CTA_COMPLETE_PAID_TEXT,
    };

    if (showFreeCta) {
        ctaButtonProps = {
            type: STYLE_FILL,
            text: CTA_COMPLETE_FREE_TEXT,
        };
    } else if (
        isPayPalSelected &&
        (isCheckoutFormEnabled ||
            isCheckoutFormSubmitting ||
            hasCheckoutFormServerError)
    ) {
        const payPalButtonStyles = isAppRebrandActive
            ? { color: ButtonColorOption.Black, shape: ButtonShapeOption.Pill }
            : {};
        ctaButtonProps = {
            node: (
                <ConnectedPaypalButton
                    isDisabled={isPayPalButtonDisabled}
                    {...payPalButtonStyles}
                />
            ),
        };
    } else if (isCheckoutFormEnabled) {
        switch (selectedPaymentMethod) {
            case PaymentMethodType.APPLE_PAY:
                trackOnce({
                    eventName: HeapEventName.applePayButtonView,
                    eventData: {
                        pathname: getRoutePathname(state),
                    },
                });
                ctaButtonProps = {
                    type: STYLE_APPLE_PAY,
                    text: applepay?.canMakePaymentsWithActiveCard
                        ? CTA_BUY_APPLE_PAY
                        : CTA_SETUP_APPLE_PAY,
                };
                break;
            case PaymentMethodType.GOOGLE_PAY:
                const maxBorderRadius = '999';
                const googlePayButtonStyles = isAppRebrandActive
                    ? { buttonRadius: maxBorderRadius }
                    : {};
                ctaButtonProps = {
                    node: <GooglePayButton {...googlePayButtonStyles} />,
                };
                break;
            case PaymentMethodType.STRIPE_CONFIRMATION_TOKEN:
                // enforcing that even the button isn't displayed if FF is off
                const stripeFeatureFlag =
                    getPaymentsLaunchStripeElementsFF(state);
                if (!stripeFeatureFlag) {
                    break;
                }
                const firstName = getBuyerFirstNameFromSurveyData(state);
                const lastName = getBuyerLastNameFromSurveyData(state);
                const email = getBuyerEmailFromSurveyData(state);
                const hasRetryError = getHasRetryPaymentError(state) || false;
                ctaButtonProps = {
                    node: (
                        <ConnectedStripeButton
                            isSubmitting={isCheckoutFormSubmitting}
                            hasErrors={hasRetryError}
                            firstName={firstName}
                            lastName={lastName}
                            email={email}
                        />
                    ),
                };
                break;
            default:
                break;
        }
    }

    return ctaButtonProps;
};

export const getHasDonationTickets = (state: CheckoutState) =>
    !!Object.values<Ticket>(getTicketsById(state)).find(
        ({ donation }) => donation,
    );

export const getHasDonationTicketsAvailable = (state: CheckoutState) =>
    !!Object.values<Ticket>(getTicketsById(state)).find(
        ({ donation, onSaleStatus }) =>
            donation && onSaleStatus === AVAILABLE_TICKET_STATUS,
    );

export const getAreAllTicketsSoldOut = (state: CheckoutState) => {
    const ticketsById = state.tickets.ticketsById;
    const ticketClasses = Object.keys(ticketsById).map(
        (ticketId) => ticketsById[ticketId],
    );

    return ticketClasses.every((ticket) => ticket.onSaleStatus === 'SOLD_OUT');
};
